import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { X } from 'lucide-react'
import { useState } from 'react'

import ScreenLayout from '@/components/layout/ScreenLayout'
import { badgeVariants } from '@/components/ui/badge-variants'
import EmptyState from '@/components/ui/EmptyState'
import NewAlertModal from '@/features/alerts/components/NewAlertModal'
import { failureMessage, portfolioFailure } from '@/features/portfolio/api/errors'
import { useHoldings, usePortfolioOverview, useProfitLoss } from '@/features/portfolio/api/queries'
import { useOnline } from '@/hooks/useOnline'
import { cn } from '@/lib/utils'
import { lastUpdatedMeta, resolveViewState, savedAtMeta } from '@/lib/view-state'
import AddTransactionModal from '@/screens/portfolio/components/add-transaction/AddTransactionModal'
import TextLink from '@/components/ui/TextLink'
import BookTotals from '@/screens/portfolio/components/holdings/BookTotals'
import DeleteHoldingDialog from '@/screens/portfolio/components/holdings/DeleteHoldingDialog'
import PortfolioFamilyHeader from '@/screens/portfolio/components/PortfolioFamilyHeader'
import {
  DEFAULT_VISIBLE_COLUMNS,
  type HoldingsColumnKey,
} from '@/screens/portfolio/components/holdings/columns'
import {
  filterBySectors,
  holdingsSummary,
  searchHoldings,
  sectorOptions,
} from '@/screens/portfolio/components/holdings/filterHoldings'
import HoldingsTable from '@/screens/portfolio/components/holdings/HoldingsTable'
import HoldingsToolsRow, {
  type HoldingsOverlay,
} from '@/screens/portfolio/components/holdings/HoldingsToolsRow'
import {
  DEFAULT_HOLDINGS_SORT,
  sortHoldings,
  type HoldingsSortKey,
} from '@/screens/portfolio/components/holdings/sortHoldings'
import { useActiveShareholderId } from '@/screens/portfolio/components/portfolio-switcher/activeProfile'

export const Route = createFileRoute('/_app/portfolio/holdings')({ component: HoldingsScreen })

/**
 * Portfolio · Holdings (Figma 997:26842, V2 `4236:76386`).
 *
 * The table, its states, and the four overlays the frame carries — Sort
 * (3448:35293), Filter (3449:19777), Columns (3761:41759) and the row menu
 * (3448:36811 / 5161:94095) — plus the tools row that triggers them and the
 * summary line their applied state changes.
 *
 * Book totals (five figures off `/overview/` and `/profit-loss/`) sit under
 * the table, as the mock (`Kde`) draws them; there is no footnote and no
 * "Mitra says" banner. Neither read gates the table: `/holdings/` alone
 * still decides what this screen shows, and the two extra reads carry their
 * own per-tile pending/error state (`BookTotals`'s own doc).
 *
 * `compactAction` — the Holdings frame drops "Add transaction" to a bare `+`
 * icon button in the header (portfolio-design-ia.md §3.2); every other screen
 * keeps the label.
 *
 * ── View state, via `resolveViewState` ──────────────────────────────────────
 *
 * `GET /holdings/` answers **200 with `data: []`** both for a member who has
 * sold everything and for a member who has never created a shareholder
 * profile — `resolve_active` returns `None` for reads rather than raising, and
 * the two causes are indistinguishable from the response alone. So an empty
 * book renders the empty state, never an error card.
 *
 * `resolveViewState` (not a hand-rolled `isPending`/`isError` ternary) is what
 * makes that hold up under two cases the old branching missed: a background
 * refetch that fails now keeps the table standing rather than swapping it for
 * an error block (data-on-screen wins), and a first load while offline reads
 * as `offline` — its own page with "Try again" — instead of the tools row and
 * nine skeleton rows sitting there forever. Failures still branch on
 * `error.code` through `portfolioFailure`, never on HTTP status: four
 * different codes share 404 with four different correct outcomes, and the
 * backend's own `error.message` is explicitly not UI copy (on a 401 it is a
 * stringified Python dict).
 *
 * ── Sort, filter, search and columns are all client-side ───────────────────
 *
 * `GET /holdings/` takes no sort, filter or pagination parameter — only
 * `shareholder_id` — so every one of the four overlays operates on the same
 * in-memory array `useHoldings()` already fetched. No new request is ever
 * made for any of them. The pipeline is search → sector filter → sort, in
 * that order: the summary line's "N of 9 holdings" counts the SECTOR filter
 * only (matching the frame's own wording), independent of what the search box
 * happens to be narrowing at the same time — and, per the design, only shows
 * at all once a sector filter is applied (4236:76386 draws no summary line
 * over an unfiltered table).
 *
 * ── Row menu → Add transaction / Edit transactions / Set an alert ──────────
 *
 * "Add transaction" opens the existing `AddTransactionModal` with
 * `presetSymbol` — a NEW entry, Company already set to the row's symbol
 * (`SCRATCH/api/PF-WP3-props.md`; this is a SECOND, independent mount of that
 * modal, not the family header's own — each caller owns its own open state,
 * exactly as that component's contract expects). "Edit transactions"
 * navigates to Activity with the symbol carried in the URL (`?symbol=`, the
 * same `Route.useSearch()` shape `/alerts` already uses), which seeds that
 * screen's own search box. "Set an alert" opens the existing `NewAlertModal`
 * in place, exactly as a watchlist row or a stock header already do — see
 * that component's own doc for why it never navigates away first.
 */
function HoldingsScreen() {
  const shareholderId = useActiveShareholderId()
  const holdings = useHoldings(shareholderId)
  const overviewQuery = usePortfolioOverview(shareholderId)
  const profitLossQuery = useProfitLoss(shareholderId)
  const navigate = useNavigate()
  const online = useOnline()
  const view = resolveViewState(holdings, { isEmpty: (rows) => rows.length === 0, online })
  const allHoldings = holdings.data ?? []

  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<HoldingsSortKey>(DEFAULT_HOLDINGS_SORT)
  const [selectedSectors, setSelectedSectors] = useState<ReadonlySet<string>>(new Set())
  const [visibleColumns, setVisibleColumns] =
    useState<ReadonlySet<HoldingsColumnKey>>(DEFAULT_VISIBLE_COLUMNS)
  const [openOverlay, setOpenOverlay] = useState<HoldingsOverlay>(null)
  const [alertSymbol, setAlertSymbol] = useState<string | null>(null)
  const [addSymbol, setAddSymbol] = useState<string | null>(null)
  const [deleteSymbol, setDeleteSymbol] = useState<string | null>(null)

  const sectorFiltered = filterBySectors(allHoldings, selectedSectors)
  const rows = sortHoldings(searchHoldings(sectorFiltered, query), sort)

  return (
    <ScreenLayout
      header={<PortfolioFamilyHeader tab="holdings" compactAction />}
      pageTitle="Holdings"
    >
      {view === 'failed' || view === 'offline' ? (
        <EmptyState
          kind={view}
          size="page"
          // Offline keeps EmptyState's own "You are offline" copy — only a
          // real failure gets this screen's title/description, exactly as
          // QueryState's own `{...(view === 'failed' ? failed : offline)}`
          // does for every other screen.
          {...(view === 'failed'
            ? {
                title: 'Your holdings did not load',
                description: failureMessage(portfolioFailure(holdings.error) ?? { kind: 'failed' }),
              }
            : {})}
          onRetry={() => {
            void holdings.refetch()
          }}
          retrying={holdings.isFetching}
          meta={
            view === 'failed'
              ? lastUpdatedMeta(holdings.dataUpdatedAt)
              : savedAtMeta(holdings.dataUpdatedAt)
          }
        />
      ) : (
        // The mock's order (`Kde`): the tools row, summary line and table as one
        // tight `gap-4` group (the table pages' toolbar-to-table gap), then Book
        // totals UNDER the table — each block a full main-column gap apart.
        <>
          <div className="flex flex-col gap-4">
            <HoldingsToolsRow
              query={query}
              onQueryChange={setQuery}
              sort={sort}
              onSortChange={setSort}
              sectors={sectorOptions(allHoldings)}
              selectedSectors={selectedSectors}
              onSectorsApply={setSelectedSectors}
              visibleColumns={visibleColumns}
              onColumnsApply={setVisibleColumns}
              openOverlay={openOverlay}
              onOpenOverlayChange={setOpenOverlay}
            />

            {/* Design draws this row only once a sector filter is applied
              (4236:76386 has no summary over the unfiltered table) — an
              unfiltered "9 holdings. Total Rs X." never renders. One removable
              chip per applied sector at the LEFT — the repo's existing
              removable-chip idiom (`badgeVariants` + a trailing ✕, as
              `FloorsheetScreen`'s either-side chip and `NewsFilters`' applied
              chips already draw one, rather than a new chip component), but
              the owner's image draws this one NEUTRAL (muted surface,
              foreground label, muted ✕) rather than the accent-gold
              `FloorsheetScreen` draws its own in — this chip alone is the
              active-filter signal, so it does not also borrow the accent
              treatment those other chips use for a live narrowing. The
              summary sentence sits right after it, and "Clear filter" is
              pinned to the RIGHT edge — lined up with Columns above, via
              `justify-between`. `flex-wrap` lets it fold to chip+text then
              Clear filter on a narrow screen, never off the side. */}
            {view !== 'loading' && selectedSectors.size > 0 ? (
              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  {[...selectedSectors].map((sector) => (
                    <button
                      key={sector}
                      type="button"
                      aria-label={`Remove the ${sector} sector filter`}
                      onClick={() => {
                        setSelectedSectors((current) => {
                          const next = new Set(current)
                          next.delete(sector)
                          return next
                        })
                      }}
                      // rest:pa — mock `pfa-chip`: the surface, tertiary 12; hover lifts it to
                      // the raised fill and the primary ink, the × with it.
                      className={cn(
                        badgeVariants({ tone: 'neutral', size: 'md' }),
                        'group/chip shrink-0 cursor-pointer gap-2 bg-card text-muted-foreground-subtle transition-colors duration-fast hover:bg-secondary hover:text-foreground',
                      )}
                    >
                      {`Sector: ${sector.toLowerCase()}`}
                      <X className="size-3 shrink-0" aria-hidden="true" />
                    </button>
                  ))}

                  <p className="text-body-xs text-muted-foreground">
                    {holdingsSummary(allHoldings, selectedSectors)}
                  </p>
                </div>

                {/* The kit's gold text link (mock: gold → #e2b755 + underline). */}
                <TextLink
                  onClick={() => {
                    setSelectedSectors(new Set())
                  }}
                >
                  Clear filter
                </TextLink>
              </div>
            ) : null}

            <HoldingsTable
              holdings={rows}
              isLoading={view === 'loading'}
              visibleColumns={visibleColumns}
              onOpenStock={(symbol) => {
                void navigate({ to: '/stocks/$symbol', params: { symbol } })
              }}
              onEditTransactions={(symbol) => {
                void navigate({ to: '/portfolio/activity', search: { symbol } })
              }}
              onSetAlert={setAlertSymbol}
              onAddTransaction={setAddSymbol}
              onDeleteHolding={setDeleteSymbol}
              emptyState={
                allHoldings.length > 0 ? (
                  // The mock's one line and its gold way back (`pfa-empty`).
                  <p className="py-6 text-center text-body-m text-muted-foreground">
                    No holdings match.{' '}
                    <TextLink
                      onClick={() => {
                        setQuery('')
                        setSelectedSectors(new Set())
                      }}
                    >
                      Show all holdings
                    </TextLink>
                  </p>
                ) : (
                  <EmptyState
                    kind="empty"
                    title="No holdings yet"
                    description="Record a buy and your positions will show up here."
                  />
                )
              }
            />
          </div>

          <BookTotals
            summary={overviewQuery.data?.summary}
            profitLoss={profitLossQuery.data}
            summaryPending={overviewQuery.isPending}
            summaryError={overviewQuery.isError}
            profitLossPending={profitLossQuery.isPending}
            profitLossError={profitLossQuery.isError}
          />
        </>
      )}

      <DeleteHoldingDialog
        symbol={deleteSymbol}
        onClose={() => {
          setDeleteSymbol(null)
        }}
      />

      <NewAlertModal
        open={alertSymbol !== null}
        onClose={() => {
          setAlertSymbol(null)
        }}
        {...(alertSymbol === null ? {} : { presetSymbol: alertSymbol })}
      />

      <AddTransactionModal
        open={addSymbol !== null}
        onOpenChange={(open) => {
          if (!open) setAddSymbol(null)
        }}
        {...(addSymbol === null ? {} : { presetSymbol: addSymbol })}
      />
    </ScreenLayout>
  )
}
